import type { CSSProperties } from 'react';
import '../components/atomic/layout/app_screen/app_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import { Arrows } from '../components/atomic/buttons/arrows/Arrows';
import { InfoPopupButton } from '../components/atomic/buttons/info_popup_button/InfoPopupButton';
import { InfoPopupFrame2 } from '../components/atomic/dialog/info_popup_frame_2/InfoPopupFrame2';
import { ScreenHeader } from '../components/atomic/layout/screen_header/ScreenHeader';
import { NavBar } from '../components/composite/nav/nav_bar/NavBar';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import { isVinylMusicDelivery } from '../../assets/preloadSounds';
import {
  LIST_ARROW_POSITIONS,
  LIST_ARROW_SCALE,
  type DeliveryListScreenModel,
} from './DeliveryListScreenModelScreen';
import { DeliveryCard } from './delivery_card/DeliveryCard';
import { AgainScreen } from './again-screen/AgainScreen';
import bodyTextSvg from './info_popup/body_text.svg';
import './info_popup/DeliveryListInfoPopup.css';
import titleTextSvg from './info_popup/title_text.svg';
import { HeaderText } from './texts/HeaderText';
import type { DeliveryListScreenController } from './DeliveryListScreenController';
import './delivery_list_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useSwipeNavigate } from '../shared/gestures/useSwipeNavigate';

export type DeliveryListScreenViewProps = ScreenViewProps<
  DeliveryListScreenModel,
  DeliveryListScreenController
>;

/** Delivery list screen stub. */
export function DeliveryListScreenView({
  model,
  controller,
}: DeliveryListScreenViewProps) {
  const swipe = useSwipeNavigate({
    axis: 'vertical',
    onSwipe: controller.handleNavigate,
    enabled: model.hasDeliveries && !model.infoOpen && !model.againOpen,
  });
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);
  const plaquesStyle = {
    '--fit-contain-inv-x': String(contain.invX),
    '--fit-contain-inv-y': String(contain.invY),
    '--fit-contain-uniform': String(contain.uniform),
  } as CSSProperties;
  const vinylInteractive =
    model.itemFrame === 'opened' &&
    isVinylMusicDelivery(model.giantId, model.orderIndex);

  return (
    <div
      className="app-screen delivery-list-screen"
      style={swipe.style}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClickCapture={swipe.onClickCapture}
    >
      <SkyAtmosphere variant="delivery" />
      <div className="delivery-list-plaques" style={plaquesStyle}>
        <ScreenHeader showSubHeader={false} />
        <HeaderText />
        {model.hasDeliveries ? (
          <DeliveryCard
            giantId={model.giantId}
            orderIndex={model.orderIndex}
            itemFrame={model.itemFrame}
            levelNameText={model.levelNameText}
            giantNameText={model.giantNameText}
            statusBanner={model.statusBanner}
            deliveryRating={model.deliveryRating}
            onAgain={controller.handleAgainClick}
            onMoreDetails={controller.handleMoreDetails}
            againDisabled={!model.canReplay}
            onItemActivate={
              vinylInteractive
                ? controller.handleToggleVinylMusic
                : undefined
            }
          />
        ) : null}
        <Arrows
          directions={['up', 'down']}
          positions={LIST_ARROW_POSITIONS}
          scale={LIST_ARROW_SCALE}
          canNavigate={{
            up: model.canNavigateUp,
            down: model.canNavigateDown,
          }}
          onNavigate={controller.handleNavigate}
        />
        <InfoPopupButton
          className={model.infoOpen ? 'info-popup-button--above-popup' : undefined}
          left={919}
          top={157}
          scale={1.503672}
          onClick={controller.handleInfoButtonClick}
        />
        {model.infoOpen ? (
          <InfoPopupFrame2
            titleSrc={titleTextSvg}
            bodySrc={bodyTextSvg}
            titleClassName="delivery-list-info-popup__title"
            bodyClassName="delivery-list-info-popup__body"
            titleAlt="All deliveries in one place."
            bodyAlt="Review successful deliveries and replay failed ones to improve your rating."
            onClose={controller.handleInfoClose}
          />
        ) : null}
      </div>
      <NavBar
        activeTab={model.activeTab}
        onTabChange={controller.handleTabChange}
      />
      {model.againOpen ? (
        <div
          className="delivery-list-plaques delivery-list-again-overlay"
          style={plaquesStyle}
        >
          <AgainScreen
            onYes={controller.handleAgainYes}
            onNo={controller.handleAgainNo}
          />
        </div>
      ) : null}
    </div>
  );
}
